import { useEffect, useRef } from 'react';
import {
  Bold, Italic, Underline, Heading1, Heading2, List, ListOrdered,
  Quote, Link as LinkIcon, Image as ImageIcon, Table as TableIcon,
  AlignLeft, AlignCenter, AlignRight, RemoveFormatting, Undo2, Redo2,
} from 'lucide-react';
import clsx from 'clsx';

interface Props {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Min editable height in tailwind units e.g. `min-h-[640px]` for an A4-ish feel. */
  className?: string;
}

/**
 * Lightweight Word-like rich text editor — a `contentEditable` surface plus
 * a sticky formatting toolbar that calls `document.execCommand`. We keep it
 * dependency-free on purpose: a polished editor (TipTap/Lexical) is overkill
 * for v1 and ships ~150KB of JS. The trade-off is that we don't get a virtual
 * doc model — but for quotations / proposals / contracts this is plenty.
 *
 * Output is HTML; that's what the document.body field stores. Backend's
 * `mdLite` renderer detects HTML and round-trips it cleanly.
 */
export function RichTextEditor({ value, onChange, disabled, placeholder, className }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  // Hydrate the contentEditable surface only when value diverges externally.
  // We don't reset on every keystroke or the caret jumps to the start.
  useEffect(() => {
    if (!ref.current) return;
    if (ref.current.innerHTML !== value) {
      ref.current.innerHTML = value || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const exec = (command: string, arg?: string) => {
    if (disabled) return;
    document.execCommand(command, false, arg);
    if (ref.current) onChange(ref.current.innerHTML);
    ref.current?.focus();
  };

  const formatBlock = (tag: string) => exec('formatBlock', tag);

  const insertLink = () => {
    const url = window.prompt('Link URL');
    if (!url) return;
    exec('createLink', url);
  };

  const insertImage = () => {
    const url = window.prompt('Image URL (paste from your hosting / clipboard)');
    if (!url) return;
    exec('insertImage', url);
  };

  // Insert a small 2-column 2-row table — users can extend rows in the DOM
  // by tabbing or by manually editing. Keeps the toolbar lightweight.
  const insertTable = () => {
    if (disabled) return;
    const html = `<table><thead><tr><th>Header 1</th><th>Header 2</th></tr></thead><tbody><tr><td>Cell</td><td>Cell</td></tr><tr><td>Cell</td><td>Cell</td></tr></tbody></table><p><br></p>`;
    document.execCommand('insertHTML', false, html);
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const handleInput = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  return (
    <div className="rounded-2xl border border-border bg-surface overflow-hidden">
      {/* Toolbar */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 border-b border-border bg-surface-alt/60 px-2 py-1.5 backdrop-blur">
        <ToolGroup>
          <ToolBtn onClick={() => exec('undo')} title="Undo (⌘Z)"><Undo2 size={14} /></ToolBtn>
          <ToolBtn onClick={() => exec('redo')} title="Redo (⌘⇧Z)"><Redo2 size={14} /></ToolBtn>
        </ToolGroup>

        <Divider />

        <ToolGroup>
          <ToolBtn onClick={() => formatBlock('h1')} title="Heading 1"><Heading1 size={14} /></ToolBtn>
          <ToolBtn onClick={() => formatBlock('h2')} title="Heading 2"><Heading2 size={14} /></ToolBtn>
          <ToolBtn onClick={() => formatBlock('p')} title="Paragraph">¶</ToolBtn>
        </ToolGroup>

        <Divider />

        <ToolGroup>
          <ToolBtn onClick={() => exec('bold')} title="Bold (⌘B)"><Bold size={14} /></ToolBtn>
          <ToolBtn onClick={() => exec('italic')} title="Italic (⌘I)"><Italic size={14} /></ToolBtn>
          <ToolBtn onClick={() => exec('underline')} title="Underline (⌘U)"><Underline size={14} /></ToolBtn>
        </ToolGroup>

        <Divider />

        <ToolGroup>
          <ToolBtn onClick={() => exec('insertUnorderedList')} title="Bullet list"><List size={14} /></ToolBtn>
          <ToolBtn onClick={() => exec('insertOrderedList')} title="Numbered list"><ListOrdered size={14} /></ToolBtn>
          <ToolBtn onClick={() => formatBlock('blockquote')} title="Quote"><Quote size={14} /></ToolBtn>
        </ToolGroup>

        <Divider />

        <ToolGroup>
          <ToolBtn onClick={() => exec('justifyLeft')} title="Align left"><AlignLeft size={14} /></ToolBtn>
          <ToolBtn onClick={() => exec('justifyCenter')} title="Align center"><AlignCenter size={14} /></ToolBtn>
          <ToolBtn onClick={() => exec('justifyRight')} title="Align right"><AlignRight size={14} /></ToolBtn>
        </ToolGroup>

        <Divider />

        <ToolGroup>
          <ToolBtn onClick={insertLink} title="Insert link"><LinkIcon size={14} /></ToolBtn>
          <ToolBtn onClick={insertImage} title="Insert image (URL)"><ImageIcon size={14} /></ToolBtn>
          <ToolBtn onClick={insertTable} title="Insert table"><TableIcon size={14} /></ToolBtn>
        </ToolGroup>

        <Divider />

        <ToolGroup>
          <ToolBtn onClick={() => exec('removeFormat')} title="Clear formatting"><RemoveFormatting size={14} /></ToolBtn>
        </ToolGroup>
      </div>

      {/* Page surface — A4-ish width, generous padding, gold drop on hover-focus */}
      <div className="bg-bg p-6">
        <div
          ref={ref}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={handleInput}
          onBlur={handleInput}
          data-placeholder={placeholder ?? 'Start writing — your blank page is ready.'}
          className={clsx(
            'mx-auto w-full max-w-[816px] rounded-md bg-white px-12 py-14 text-[14.5px] leading-relaxed text-[#1a1a1a] shadow-soft',
            'prose-sm focus:outline-none focus:ring-4 focus:ring-primary-soft',
            'wcrm-doc-surface',
            disabled && 'opacity-60 pointer-events-none',
            className,
          )}
          style={{ minHeight: '720px' }}
        />
      </div>

      {/* Default styles for the doc surface — kept here so the editor is drop-in.
          The host app already loads Tailwind so the !-suffixed selectors win
          even without a Tailwind plugin. */}
      <style>{`
        .wcrm-doc-surface:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        .wcrm-doc-surface h1 { font-size: 1.6rem; font-weight: 700; margin: 1.2rem 0 0.5rem; letter-spacing: -0.01em; }
        .wcrm-doc-surface h2 { font-size: 1.3rem; font-weight: 700; margin: 1.1rem 0 0.45rem; letter-spacing: -0.005em; }
        .wcrm-doc-surface h3 { font-size: 1.1rem; font-weight: 700; margin: 1rem 0 0.4rem; }
        .wcrm-doc-surface p { margin: 0 0 0.75rem; }
        .wcrm-doc-surface ul, .wcrm-doc-surface ol { padding-left: 1.5rem; margin: 0 0 0.75rem; }
        .wcrm-doc-surface li { margin-bottom: 0.25rem; }
        .wcrm-doc-surface a { color: #d4a849; text-decoration: underline; }
        .wcrm-doc-surface blockquote { border-left: 3px solid #d4a849; padding: 0.4rem 0.8rem; margin: 0.6rem 0; color: #555; background: #faf6ec; border-radius: 0 6px 6px 0; }
        .wcrm-doc-surface table { width: 100%; border-collapse: collapse; margin: 0.5rem 0 0.75rem; }
        .wcrm-doc-surface th, .wcrm-doc-surface td { border: 1px solid #e5e7eb; padding: 6px 10px; font-size: 13.5px; vertical-align: top; }
        .wcrm-doc-surface th { background: #faf6ec; font-weight: 600; text-align: left; }
        .wcrm-doc-surface img { max-width: 100%; border-radius: 6px; margin: 0.4rem 0; }
      `}</style>
    </div>
  );
}

// ─── Toolbar primitives ──────────────────────────────────────────────────────

function ToolGroup({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center">{children}</div>;
}

function Divider() {
  return <div className="mx-1 h-5 w-px bg-border" />;
}

function ToolBtn({ onClick, title, children }: { onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="flex h-7 min-w-[28px] items-center justify-center rounded-md px-1.5 text-text-secondary transition-colors hover:bg-primary-soft hover:text-primary"
    >
      {children}
    </button>
  );
}
